import { navigate } from 'gatsby';
import React, { FC } from 'react';

import BaseCard from '@ui/atoms/BaseCard';
import BasicText from '@ui/atoms/BasicText';
import Breadcrumb from '@ui/atoms/Breadcrumb';
import { CenteredLink } from '@ui/atoms/CenteredLink';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';

import BillCard from '@ui/organisms/NewBillCard';

import fullWidth from '@ui/styled/FullWidth';
import spaced from '@ui/styled/Spaced';

import { IInvoice } from '@hooks/useMemberInvoice';

export interface IBillDetailsContent {
	title: string;
	breadcrumbLabel: string;
	paidBillLabel: string;
	paidBillDisclaimer: string;
	unpaidBillDisclaimer: string;
	expiredBillDisclaimer: string;
	unpaidBillShipping: string;
	billNotFoundTitle: string;
	billNotFoundText: string;
	billNotFoundButtonText: string;
}

interface IBillDetailsProps {
	pageContent: IBillDetailsContent;
	invoiceId: string;
	invoice?: IInvoice;
}

const FullWidthColumn = fullWidth<IFlexColumn>(FlexColumn);
const SpacedColumn = spaced<IFlexColumn>(FullWidthColumn);

interface IBillNotFoundProps {
	title: string;
	text: string;
	buttonText: string;
	invoiceId?: string;
}
const BillNotFound = ({ title, text, buttonText, invoiceId }: IBillNotFoundProps) => (
	<SpacedColumn gap="xxs">
		<BasicText variant="fineprint" color="placeholderGray" tint="darken40">
			{invoiceId}
		</BasicText>
		<BaseCard bgColor="white" borderColor="red" radius="md" shadow>
			<SpacedColumn gap="md">
				<HeadingText>{title}</HeadingText>
				<BasicText variant="body">{text}</BasicText>
				<CenteredLink to="/app/member/billing/bills" variant="underline">
					{buttonText}
				</CenteredLink>
			</SpacedColumn>
		</BaseCard>
	</SpacedColumn>
);

interface IInvoiceDetails {
	title: string;
	invoice: IInvoice;
}
const InvoiceDetails = ({ title, invoice }: IInvoiceDetails) => (
	<SpacedColumn gap="md">
		<HeadingText>{title}</HeadingText>
		<SpacedColumn gap="xxs">
			<BasicText variant="fineprint" color="placeholderGray" tint="darken40">
				Invoice {invoice.number}
			</BasicText>
			<BillCard bill={invoice} handleClick={() => navigate('/app/member/billing/bills/paid')} />
		</SpacedColumn>
	</SpacedColumn>
);

const BillDetails: FC<IBillDetailsProps> = ({ pageContent, invoice, invoiceId }) => {
	const { title, breadcrumbLabel, billNotFoundTitle, billNotFoundText, billNotFoundButtonText } =
		pageContent;
	return (
		<CenteredGridItem>
			<SpacedColumn gap="sm" marginBottom="lg">
				<Breadcrumb href="/app/member/billing/bills" label={breadcrumbLabel} />
				{invoice ? (
					<InvoiceDetails title={title} invoice={invoice} />
				) : (
					<BillNotFound
						title={billNotFoundTitle}
						text={billNotFoundText}
						buttonText={billNotFoundButtonText}
						invoiceId={invoiceId}
					/>
				)}
			</SpacedColumn>
		</CenteredGridItem>
	);
};

export default BillDetails;
